import React, { useMemo, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
    useExpanded,
    useFilters,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
} from 'react-table';
import { withTranslation } from 'react-i18next';
import {
    Box,
    DaInput,
    SearchIcon,
    InternalGrid,
    ModalProvider,
    ModalControl,
    ButtonGroup,
} from '@paygreen/paygreen-ui';
import { useDebounce } from '../../utils/hooks';
import { IsBigDesktop } from '../../utils/tools';
import { statusMatching } from '../../utils/constants';
import { PaginationTable, StatusFilterTable, Disabler } from '../../components';
import ModifyProduct from './modifyProduct/ModifyProduct';
import MatchingDaTable from './MatchingDaTable';
import TableCheckbox from './TableCheckbox';
import ProductsButtons from './ProductsButtons';
import GroupedActionsButtons from './groupedProducts/GroupedActionsButtons';

const MatchingTable = ({
    t,
    data,
    fetchData,
    isLoading,
    pageCount: controlledPageCount,
    totalItems,
    filters,
    setFilters,
    handleRefreshData,
    refresh,
    resetDebounce,
    setIsLoading,
}) => {
    const debouncedFilters = useDebounce(filters, 250, resetDebounce);
    const [modalData, setModalData] = useState();

    const handleIndex = useCallback(
        (value) => {
            setFilters({
                ...filters,
                pageIndex: value,
            });
        },
        [setFilters, filters],
    );

    const setPageSize = (size) => {
        setFilters({
            ...filters,
            pageSize: size,
            pageIndex: 0,
            activePageReset: true,
        });
    };

    const handleStatusFilter = useCallback(
        (value) => {
            setFilters({
                ...filters,
                pageIndex: 0,
                statusFilter: value,
                activePageReset: true,
            });
        },
        [setFilters, filters],
    );

    const handleProductName = useCallback(
        (e) => {
            const { value } = e.target;

            if (
                // we start research only with minimum of 3 characters and when back to empty input
                !value.length ||
                value.length > 2
            ) {
                setIsLoading(true);
                setFilters({
                    ...filters,
                    productName: value,
                    activePageReset: true,
                });
            }
        },
        [setFilters, filters, setIsLoading],
    );

    const columns = useMemo(() => {
        const renderUnit = (ref) => {
            const rowUnit = ref.emissionFactorUnit || ref.monetaryRatioUnit;
            const cleanUnit = rowUnit?.split('/')[1];
            if (cleanUnit === 'euro') {
                return t('matching.table.perEuro');
            }
            if (cleanUnit === 'unit') {
                return '';
            }
            return cleanUnit;
        };
        return [
            {
                Header: t('matching.table.productExternalReference'),
                accessor: 'productExternalReference',
            },
            {
                Header: t('matching.table.productName'),
                accessor: 'productName',
                Filter: (
                    <DaInput
                        id="searchInput"
                        onChange={handleProductName}
                        placeholder={t('user.list.table.input')}
                        fieldSize="sm"
                        icon={<SearchIcon />}
                        defaultValue={filters.productName}
                    />
                ),
            },
            {
                Header: t('matching.table.categories'),
                id: 'productCategories',
                accessor: (d) => [
                    d.externalProductCategory1,
                    d.externalProductCategory2,
                    d.externalProductCategory3,
                ],
            },
            {
                Header: t('matching.table.CO2'),
                id: 'emissionFactor',
                accessor: (d) => [
                    d.emissionFactorValue || d.monetaryRatioValue,
                    renderUnit(d),
                ],
            },
            {
                Header: t('matching.table.estimationType'),
                accessor: (row) =>
                    row.emissionExternalId
                        ? statusMatching[1]
                        : row.monetaryRatioMatch
                        ? statusMatching[0]
                        : statusMatching[2],
                id: 'matchingType',
                Filter: ({ filter, ...rest }) => (
                    <StatusFilterTable
                        defaultValue={filters.statusFilter}
                        onChange={handleStatusFilter}
                        statusConstants={statusMatching}
                        i18TextBase="matching.table.status"
                        {...rest}
                    />
                ),
                filter: 'includes',
            },
            {
                Header: 'Actions',
                accessor: '',
            },
        ];
    }, [
        t,
        handleStatusFilter,
        filters.statusFilter,
        filters.productName,
        handleProductName,
    ]);

    const {
        headerGroups,
        prepareRow,
        page,
        pageCount,
        state: { selectedRowIds },
    } = useTable(
        {
            columns,
            data,
            initialState: { pageIndex: 0, pageSize: 25 },
            manualPagination: true,
            autoResetPage: false,
            pageCount: controlledPageCount,
            manualSortBy: true,
            autoResetFilters: false,
            // we ponctually override table internal state to resetPage index with component SelectColumnFilter when needed
            useControlledState: (state) => {
                return {
                    ...state,
                    pageIndex: debouncedFilters.pageIndex,
                    pageSize: debouncedFilters.pageSize,
                };
            },
        },
        useFilters,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect,

        // Here we will use a plugin to add our selection column
        (hooks) => {
            hooks.visibleColumns.push((columns) => {
                return [
                    {
                        id: 'selection',
                        // The header can use the table's getToggleAllRowsSelectedProps method
                        // to render a checkbox
                        Header: ({ getToggleAllRowsSelectedProps }) => (
                            <TableCheckbox
                                inputId="all"
                                {...getToggleAllRowsSelectedProps()}
                            />
                        ),
                    },
                    ...columns,
                ];
            });
        },
    );

    useEffect(() => {
        fetchData({
            debouncedFilters,
        });
    }, [fetchData, debouncedFilters, refresh]);

    return (
        <InternalGrid
            displayType="grid"
            gridGap="md"
            gridTemplateColumns="1fr"
            blockPadding="none"
            justifyItems={IsBigDesktop() ? 'start' : 'stretch'}
            hasStaticWidth={IsBigDesktop() ? false : true}
        >
            <Disabler>
                <ButtonGroup align="left" marginBottom="xs" marginTop="xs">
                    <InternalGrid
                        displayType="grid"
                        gridTemplateColumns="auto auto auto"
                        gridGap="md"
                        alignItems="center"
                    >
                        {!Object.keys(selectedRowIds).length && (
                            <ProductsButtons
                                isLoading={isLoading}
                                setIsLoading={setIsLoading}
                                handleRefreshData={handleRefreshData}
                            />
                        )}

                        {!!Object.keys(selectedRowIds).length && (
                            <GroupedActionsButtons
                                selectedRows={Object.keys(selectedRowIds)}
                                dataMatching={data}
                                handleRefreshData={handleRefreshData}
                            />
                        )}
                    </InternalGrid>
                </ButtonGroup>
            </Disabler>

            <ModalProvider>
                <Box>
                    <MatchingDaTable
                        headerGroups={headerGroups}
                        prepareRow={prepareRow}
                        page={page}
                        isLoading={isLoading}
                        data={data}
                        selectedRowIds={selectedRowIds}
                        handleRefreshData={handleRefreshData}
                        setModalData={setModalData}
                    />

                    {totalItems > 25 && (
                        <PaginationTable
                            setPageSize={setPageSize}
                            pageCount={pageCount || 1}
                            pageIndex={debouncedFilters.pageIndex}
                            gotoPage={handleIndex}
                        />
                    )}
                </Box>

                <ModalControl>
                    <ModifyProduct
                        productData={modalData?.productData}
                        mode={modalData?.mode}
                        handleRefreshData={handleRefreshData}
                        openMatching={modalData?.openMatching}
                    />
                </ModalControl>
            </ModalProvider>
        </InternalGrid>
    );
};

MatchingTable.propTypes = {
    data: PropTypes.array.isRequired,
    fetchData: PropTypes.func.isRequired,
    pageCount: PropTypes.number,
    isLoading: PropTypes.bool,
    totalItems: PropTypes.number,
    setSelectedRows: PropTypes.func,
    filters: PropTypes.object,
    setFilters: PropTypes.func,
    handleProductName: PropTypes.func,
    refresh: PropTypes.bool,
    resetDebounce: PropTypes.bool,
    setIsLoading: PropTypes.func,
};

MatchingTable.defaultProps = {
    isLoading: false,
    pageCount: 0,
    refresh: false,
    resetDebounce: false,
};

export default withTranslation('common')(MatchingTable);
